import type { InsightConnectivityRef, InsightGraphNode, InsightTopologyResponse } from "../../types/insightApi";
import { INSIGHT_CONTAINER_NODE_TYPES, insightNodeTypeFor, type InsightSemanticNodeType } from "./topologyNodes";
import { normalizeCanonicalTopologyNodes } from "./canonicalDigest";

/**
 * Flat, tabular projection of a topology response for the CTX-* "List"
 * representation — the readable alternative to the canvas when a source is a
 * big *flat* pile of route tables / gateways (a backbone reflector), where a
 * spatial diagram with no containment and no edges degrades into an
 * illegible wall of near-identical cards.
 *
 * This deliberately reuses the canvas's own normalization and classification
 * (`normalizeCanonicalTopologyNodes` + `insightNodeTypeFor`) so the two
 * representations describe the same resources by the same rules — the list is
 * a different *view* of the digest, never a second interpretation of it. It
 * reads server-attached decorations (`routeCount`, `connectivity`,
 * `effectiveRouteTable`, `status`) as-is and never reconstructs relationships
 * from `edges` (CTX-113 edge-free policy).
 */

/** Coarse resource family used for the type column/label and (future) type filtering. Containers (VPC/subnet/region/AZ) and synthetic summary/overflow cards are never rows. */
export type InsightResourceCategory =
	| "routeTable"
	| "gateway"
	| "eni"
	| "instance"
	| "securityGroup"
	| "regionalService"
	| "other";

export interface InsightResourceTarget {
	kind: InsightConnectivityRef["kind"];
	label: string;
}

export interface InsightResourceRow {
	id: string;
	category: InsightResourceCategory;
	/** Human type label, specialized for gateways ("Internet gateway" / "NAT gateway" / …). */
	typeLabel: string;
	name: string;
	nativeId: string;
	/** Nearest containing VPC/VCN label, else the nearest region/area label, else "" when the node is root-parented (the flat case). */
	scopeLabel: string;
	/** Route tables only — total individual-route count, as one number. */
	routeCount: number | null;
	/** Route tables only — proven connectivity targets (already deduped server-side). */
	targets: InsightResourceTarget[];
	/** Route tables only — how many subnets resolve to this table (read from subnets' `effectiveRouteTable`, not edges). */
	associatedSubnets: number | null;
	/** ENIs only — coarse health signal; `null` where the concept doesn't apply. */
	status: "ok" | "warning" | "error" | null;
}

/** Semantic node types that become list rows (everything that isn't a container boundary or a synthetic summary/overflow card). */
const ROW_CATEGORY_BY_TYPE: Partial<Record<InsightSemanticNodeType, InsightResourceCategory>> = {
	insightRouteTable: "routeTable",
	insightGateway: "gateway",
	insightEni: "eni",
	insightInstance: "instance",
	insightSecurityGroup: "securityGroup",
	insightRegionalService: "regionalService",
	insightUnresolved: "other",
};

const CATEGORY_LABEL: Record<InsightResourceCategory, string> = {
	routeTable: "Route table",
	gateway: "Gateway",
	eni: "Network interface",
	instance: "Instance",
	securityGroup: "Security group",
	regionalService: "Regional service",
	other: "Other",
};

/** Specific gateway sub-kind → human label, so a wall of "Gateway" rows reads as the mix it actually is. Keys cover both the short digest kinds and the canonical camelCase forms (lowercased). */
const GATEWAY_KIND_LABEL: Record<string, string> = {
	igw: "Internet gateway",
	internetgateway: "Internet gateway",
	"egress-only-igw": "Egress-only IGW",
	eni: "ENI (appliance next hop)",
	eigw: "Egress-only IGW",
	egressonlyinternetgateway: "Egress-only IGW",
	nat: "NAT gateway",
	natgateway: "NAT gateway",
	tgw: "Transit gateway",
	transitgateway: "Transit gateway",
	drg: "Dynamic routing gateway",
	vgw: "VPN gateway",
	vpngateway: "VPN gateway",
	virtualprivategateway: "VPN gateway",
	sgw: "Service gateway",
	peering: "VPC peering",
	vpcpeeringconnection: "VPC peering",
	endpoint: "VPC endpoint",
	vpcendpoint: "VPC endpoint",
	gateway_endpoint: "Gateway endpoint",
	s3_gateway_endpoint: "Gateway endpoint",
	"direct-connect": "Direct Connect",
	direct_connect: "Direct Connect",
	directconnectgateway: "Direct Connect",
	dx: "Direct Connect",
};

function stringField(data: Record<string, unknown> | undefined, key: string): string | undefined {
	const value = data?.[key];
	return typeof value === "string" && value.length > 0 ? value : undefined;
}

function categoryOf(node: InsightGraphNode): InsightResourceCategory | null {
	const semanticType = insightNodeTypeFor(node.type ?? (node.data?.kind as string | undefined), node.kind);
	if (INSIGHT_CONTAINER_NODE_TYPES.has(semanticType)) return null;
	return ROW_CATEGORY_BY_TYPE[semanticType] ?? null;
}

function typeLabelOf(category: InsightResourceCategory, node: InsightGraphNode): string {
	if (category !== "gateway") return CATEGORY_LABEL[category];
	const kind = (node.type ?? (node.data?.kind as string | undefined) ?? "").toLowerCase();
	return GATEWAY_KIND_LABEL[kind] ?? "Gateway";
}

/**
 * Nearest ancestor label to show as the row's "Scope": prefer the containing
 * VPC/VCN, falling back to the nearest region/area/AZ boundary. Returns "" for
 * a root-parented node — the honest empty in the flat backbone case, never a
 * fabricated container.
 */
function scopeLabelOf(node: InsightGraphNode, byId: Map<string, InsightGraphNode>): string {
	const seen = new Set<string>();
	let vpcLabel = "";
	let containerLabel = "";
	let current = node.parentId ? byId.get(node.parentId) : undefined;
	while (current && !seen.has(current.id)) {
		seen.add(current.id);
		const semanticType = insightNodeTypeFor(current.type ?? (current.data?.kind as string | undefined), current.kind);
		const label = stringField(current.data, "label") ?? "";
		if (semanticType === "insightVpc" && label) {
			vpcLabel = label;
			break; // A VPC is the most specific useful scope — stop at the first one.
		}
		if (!containerLabel && INSIGHT_CONTAINER_NODE_TYPES.has(semanticType) && label) {
			containerLabel = label;
		}
		current = current.parentId ? byId.get(current.parentId) : undefined;
	}
	return vpcLabel || containerLabel;
}

function targetsOf(node: InsightGraphNode): InsightResourceTarget[] {
	const connectivity = node.data?.connectivity;
	if (!Array.isArray(connectivity)) return [];
	return (connectivity as InsightConnectivityRef[])
		.filter((item) => item && typeof item.kind === "string")
		.map((item) => ({ kind: item.kind, label: item.label }));
}

/**
 * Build the tabular row set. Route tables and gateways come first (the flat
 * case this view exists for), then instances/ENIs/security groups/regional
 * services, each group alphabetized by name — a stable default order the
 * table's own sorting can override.
 */
export function buildResourceRows(topology: InsightTopologyResponse | null | undefined): InsightResourceRow[] {
	if (!topology?.nodes?.length) return [];
	const nodes = normalizeCanonicalTopologyNodes(topology.nodes);
	const byId = new Map(nodes.map((node) => [node.id, node]));

	// Count subnet -> route-table associations once, from subnets' resolved
	// effectiveRouteTable decoration (not edges), so each route-table row can
	// show how many subnets point at it.
	const subnetsByRouteTable = new Map<string, number>();
	for (const node of nodes) {
		const semanticType = insightNodeTypeFor(node.type ?? (node.data?.kind as string | undefined), node.kind);
		if (semanticType !== "insightSubnet") continue;
		const effectiveRouteTable = node.data?.effectiveRouteTable as { id?: string } | null | undefined;
		const rtId = effectiveRouteTable?.id;
		if (rtId) subnetsByRouteTable.set(rtId, (subnetsByRouteTable.get(rtId) ?? 0) + 1);
	}

	const rows: InsightResourceRow[] = [];
	for (const node of nodes) {
		const category = categoryOf(node);
		if (!category) continue;
		const name = stringField(node.data, "label") ?? node.id;
		const nativeId = stringField(node.data, "nativeId") ?? node.id;
		const routeCount = category === "routeTable" && typeof node.data?.routeCount === "number" ? node.data.routeCount : null;
		const statusRaw = node.data?.status;
		const status = statusRaw === "ok" || statusRaw === "warning" || statusRaw === "error" ? statusRaw : null;
		rows.push({
			id: node.id,
			category,
			typeLabel: typeLabelOf(category, node),
			name,
			nativeId,
			scopeLabel: scopeLabelOf(node, byId),
			routeCount,
			targets: category === "routeTable" ? targetsOf(node) : [],
			associatedSubnets: category === "routeTable" ? subnetsByRouteTable.get(node.id) ?? 0 : null,
			status,
		});
	}

	const categoryOrder: InsightResourceCategory[] = [
		"routeTable",
		"gateway",
		"instance",
		"eni",
		"securityGroup",
		"regionalService",
		"other",
	];
	return rows.sort((a, b) => {
		const byCategory = categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category);
		if (byCategory !== 0) return byCategory;
		return a.name.localeCompare(b.name);
	});
}

/** Free-text filter across the columns a user would scan by — name, id, type, scope, and target labels. */
export function filterResourceRows(rows: InsightResourceRow[], query: string): InsightResourceRow[] {
	const needle = query.trim().toLowerCase();
	if (!needle) return rows;
	return rows.filter((row) => {
		if (row.name.toLowerCase().includes(needle)) return true;
		if (row.nativeId.toLowerCase().includes(needle)) return true;
		if (row.typeLabel.toLowerCase().includes(needle)) return true;
		if (row.scopeLabel.toLowerCase().includes(needle)) return true;
		return row.targets.some((target) => target.label.toLowerCase().includes(needle) || target.kind.toLowerCase().includes(needle));
	});
}

/** Below this many rows the canvas is fine; at or above it a flat set is unreadable as cards and the list is the better default. */
export const LIST_DEFAULT_ROW_THRESHOLD = 24;

/**
 * Whether the List representation should be the *default* for a topology. True
 * only for the case this view targets: many resource rows with (almost) no VPC
 * containment — i.e. the flat backbone pile. A normal nested VPC topology (a
 * handful of VPCs with subnets) keeps the canvas, which reads well there.
 * User's explicit toggle always wins over this; it only picks the initial view.
 */
export function shouldDefaultToList(topology: InsightTopologyResponse | null | undefined): boolean {
	if (!topology?.nodes?.length) return false;
	const nodes = normalizeCanonicalTopologyNodes(topology.nodes);
	let vpcCount = 0;
	let rowCount = 0;
	for (const node of nodes) {
		const semanticType = insightNodeTypeFor(node.type ?? (node.data?.kind as string | undefined), node.kind);
		if (semanticType === "insightVpc") vpcCount += 1;
		if (!INSIGHT_CONTAINER_NODE_TYPES.has(semanticType) && ROW_CATEGORY_BY_TYPE[semanticType]) rowCount += 1;
	}
	return rowCount >= LIST_DEFAULT_ROW_THRESHOLD && vpcCount <= 1;
}
